// @ts-strict-ignore
import React, { type ReactNode, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { axiosGet } from '../util/api'
import { useUser } from '../hooks/useUser'
import { Callout } from '../components/ui/callout'
import { Layout } from '../components/Layout'
import { useTableHooksFromStore } from '../packages/blueprint-components/src/useTableHooks'
import { cents, money } from '../util/formatters'
import { Columns } from '../columns'
import { CampaignSelection } from '../components/selectors/CampaignSelector'
import { Toasts } from '../components/toaster/Toast'
import type { State } from '../reducers'
import { type AgGridColumnDef, AgGridTable, DummyHeader } from '../components/table2/AgGridTable'
import { TableStore } from '../components/table2/table_store'
import { userStore } from '../stores/user_store'
import { useSavedFiltersStore } from '../hooks/useSavedFiltersStore'
import type { CellProps } from '../components/table2/ad_grid_columns'
import type { EntityId } from '../types/constants'

type BidRecommendation = {
    item?: string
    current_bid?: number
    actual_clicks?: number
    actual_orders?: number
    recommended_bid?: number
    competitor_clicks?: number
    competitor_orders?: number
    keyword?: {
        keyword_text: string
        bid: number
    }
    target?: {
        bid: number
    }
    actual: {
        clicks: number
        orders: number
    }
    recommendations?: {
        bid: number
        clicks: {
            lower: number
            upper: number
        }
        orders: {
            lower: number
            upper: number
        }
    }[]
}

const agGridComponent = (component: unknown): ReactNode => component as ReactNode

const ItemCellRenderer = ({ data, context }: CellProps<BidRecommendation>) => {
    if (data.keyword) {
        return data.keyword.keyword_text
    } else if (data.target) {
        return Columns.TargetingExpression.cell(data.target, {
            screenshotMode: context.userHook.screenshotMode,
            mutateItems: (v: BidRecommendation[]) => context.store.setItems(v),
            props: {},
        })
    }
}

const CurrentBidCellRenderer = ({ data }: CellProps<BidRecommendation>) => {
    if (data.keyword) {
        return cents(data.keyword.bid)
    } else if (data.target) {
        return cents(data.target.bid)
    }
}

const ActualClicksCellRenderer = ({ data }: CellProps<BidRecommendation>) => data.actual.clicks

const ActualOrdersCellRenderer = ({ data }: CellProps<BidRecommendation>) => data.actual.orders

const RecommendedBidCellRenderer = ({ data }: CellProps<BidRecommendation>) => {
    if (!data.recommendations) {
        return null
    }

    return data.recommendations.map((recommendation, i) => (
        <div key={i}>{money(recommendation.bid)}</div>
    ))
}

const CompetitorClicksCellRenderer = ({ data }: CellProps<BidRecommendation>) => {
    if (!data.recommendations) {
        return null
    }

    return data.recommendations.map((recommendation, i) => (
        <div key={i}>
            {recommendation.clicks.lower} - {recommendation.clicks.upper}
        </div>
    ))
}

const CompetitorOrdersCellRenderer = ({ data }: CellProps<BidRecommendation>) => {
    if (!data.recommendations) {
        return null
    }

    return data.recommendations.map((recommendation, i) => (
        <div key={i}>
            {recommendation.orders.lower} - {recommendation.orders.upper}
        </div>
    ))
}

export const BidRecommendations = ({ nested = false }): JSX.Element => {
    const profile = useSelector<State, EntityId | null>((state) => state.user.profile)
    const { activeProfile } = useUser()
    const [campaign, setCampaign] = useState(null)
    const [store] = useState<TableStore<BidRecommendation>>(
        new TableStore(userStore, 'BidRecommendations'),
    )
    const savedFiltersStore = useSavedFiltersStore()

    const load = () => {
        console.log('loading with', profile, campaign)
        if (!profile || !campaign) {
            return Promise.resolve()
        }

        return axiosGet(
            'api/v2/theme-bid-recommendations',
            { campaign_id: campaign.campaign_id },
            true,
        ).catch((err) => {
            if (err.message) {
                Toasts.error(err.message)
            }

            return err
        })
    }
    const tableHooks = useTableHooksFromStore(() => load(), {}, true, store)

    useEffect(() => {
        tableHooks.reload()
    }, [])

    useEffect(() => {
        console.log('reloading')
        tableHooks.reload()
    }, [profile, campaign?.campaign_id])

    const columns = useMemo<AgGridColumnDef<BidRecommendation>[]>(() => {
        const columns: AgGridColumnDef<BidRecommendation>[] = [
            {
                field: 'item',
                headerComponent: agGridComponent(DummyHeader),
                cellRenderer: agGridComponent(ItemCellRenderer),
            },
            {
                field: 'current_bid',
                headerName: 'Current Bid',
                headerComponent: agGridComponent(DummyHeader),
                cellRenderer: agGridComponent(CurrentBidCellRenderer),
            },
            {
                field: 'actual_clicks',
                headerName: 'Your 7D Clicks',
                headerComponent: agGridComponent(DummyHeader),
                cellRenderer: agGridComponent(ActualClicksCellRenderer),
            },
            {
                field: 'actual_orders',
                headerName: 'Your 7D Orders',
                headerComponent: agGridComponent(DummyHeader),
                cellRenderer: agGridComponent(ActualOrdersCellRenderer),
            },
            {
                field: 'recommended_bid',
                headerName: 'Recommended Bid',
                headerComponent: agGridComponent(DummyHeader),
                cellRenderer: agGridComponent(RecommendedBidCellRenderer),
            },
            {
                field: 'competitor_clicks',
                headerName: 'Competitors 7D Clicks',
                headerComponent: agGridComponent(DummyHeader),
                cellRenderer: agGridComponent(CompetitorClicksCellRenderer),
            },
            {
                field: 'competitor_orders',
                headerName: 'Competitors 7D Orders',
                headerComponent: agGridComponent(DummyHeader),
                cellRenderer: agGridComponent(CompetitorOrdersCellRenderer),
            },
        ]

        return columns
    }, [activeProfile])

    const breadcrumbs = [{ text: 'Tools' }, { href: '', text: 'Bid Recommendations' }]

    return (
        <Layout breadcrumbs={breadcrumbs} nested={nested}>
            <Callout className="mb-4" intent="primary" title="Beta Feature" icon="info-sign">
                <p className="max-w-3xl">
                    Bid recommendations currently a beta feature that is still rough around the
                    edges. This is some brand new data coming from Amazon as we expect to see
                    improvements here in the future. Currently, bid recommendations have a few
                    limitations:
                    <ul className="ml-4 mb-2 list-disc">
                        <li>do not support product targeting</li>
                        <li>
                            are currently limited to campaigns with 25 or less keywords or targets
                        </li>
                        <li>
                            have limited very support for Keyword based bid recommendations,
                            particularly if they are long-tail keywords
                        </li>
                    </ul>
                    In testing, we have found best results from bid recommendations to come from
                    Auto campaigns.
                </p>
            </Callout>
            <div className="mb-2">
                Choose one of your campaigns to see your 7 day clicks and orders, Amazon's bid
                recommendations, and your competitor's 7 day clicks and orders for similar products.
                Using this, you can find keywords or targets where you could potentially be getting
                more sales by increasing your bids.
            </div>
            <CampaignSelection
                disabled={tableHooks.loading}
                value={campaign}
                onChange={(v) => setCampaign({ ...v })}
                className="mb-4"
            />

            <AgGridTable
                name="BidRecommendations"
                showHeader={false}
                tableHooks={tableHooks}
                store={store}
                savedFiltersStore={savedFiltersStore}
                dateSelect={false}
                // search
                // searchPlaceholder="Search by keyword"
                columns={columns}
                showPerPage={false}
            />
        </Layout>
    )
}
